import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { locations } from '../lib/storage';

const Locations: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );
    const section = sectionRef.current;
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <Box
      id="locations"
      ref={sectionRef}
      bg="paleOak"
      py={{ base: 16, md: 24 }}
      position="relative"
    >
      <Container maxW="1400px">
        <VStack
          spacing={{ base: 10, md: 14 }}
          align="stretch"
          opacity={isVisible ? 1 : 0}
          transform={isVisible ? 'translateY(0)' : 'translateY(40px)'}
          transition="all 0.8s ease-out"
        >
          <VStack align="center" spacing={4} textAlign="center">
            <Heading
              as="h2"
              fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
              color="black"
              fontWeight={700}
              lineHeight={1.1}
            >
              Nossas Unidades
            </Heading>
            <Text color="black" fontSize={{ base: 'md', md: 'lg' }} maxW="760px" lineHeight="1.7" fontWeight={500}>
              Atendimento presencial e unidades de apoio em diferentes regiões,
              com suporte digital para clientes em todo o Brasil.
            </Text>
            <Box w="80px" h="4px" bg="black" borderRadius="2px" />
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={{ base: 6, md: 8 }}>
            {locations.map((location, index) => (
              <Box
                key={location.id}
                bg="bone"
                border="1px solid"
                borderColor="charcoal"
                borderRadius="8px"
                overflow="hidden"
                opacity={isVisible ? 1 : 0}
                transform={isVisible ? 'translateY(0)' : 'translateY(32px)'}
                transition="all 0.7s ease-out"
                style={{ transitionDelay: `${0.08 + index * 0.05}s` }}
              >
                <Image
                  src={location.image}
                  alt={`Unidade ${location.city} - ${location.address}`}
                  objectFit="cover"
                  w="100%"
                  h={{ base: '210px', md: '190px', lg: '210px' }}
                  loading="lazy"
                />
                <VStack align="flex-start" spacing={3} p={{ base: 5, md: 6 }}>
                  <Box>
                    <Heading as="h3" fontSize={{ base: 'xl', md: '2xl' }} color="black" fontWeight={700}>
                      {location.city}
                    </Heading>
                    <Text color="shadowGrey" fontWeight={800} fontSize="sm">
                      {location.state}
                    </Text>
                  </Box>
                  <Text color="black" fontSize="sm" lineHeight="1.6" fontWeight={500}>
                    {location.address}
                  </Text>
                  {location.cep && (
                    <Text color="shadowGrey" fontSize="sm" lineHeight="1.5" fontWeight={600}>
                      {location.cep}
                    </Text>
                  )}
                  {location.phone && (
                    <Text color="black" fontSize="sm" fontWeight={800}>
                      Tel: {location.phone}
                    </Text>
                  )}
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Locations;
